import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe2, Star, Sparkles, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SearchPick } from "@/components/SearchPick";
import { api } from "@/lib/api";

const STEPS = [
  { icon: Globe2, color: "#34D399", title: "Where have you been?", hint: "Pick a country you've visited. It turns green on your map." },
  { icon: Star, color: "#F59E0B", title: "Where do you want to go?", hint: "Add the first place on your Want to go list." },
  { icon: Sparkles, color: "#FB7185", title: "One thing before you die", hint: "Write your first dream — big or small." },
];

const Dots = ({ step }) => (
  <div className="flex gap-1.5" data-testid="tour-progress">
    {STEPS.map((s, i) => <span key={i} className="h-1.5 rounded-full transition-[width,background-color] duration-300" style={{ width: i === step ? 28 : 10, background: i <= step ? s.color : "#334155" }} />)}
  </div>
);

export const WelcomeTour = ({ data, b, names }) => {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  const empty = data && !data.visited.length && !data.wishlist.length && !data.goals.length;
  const [step, setStep] = useState(0);
  const [closed, setClosed] = useState(false);
  const [dream, setDream] = useState("");
  if (!settings || settings.tour_done || closed || (step === 0 && !empty)) return null;

  const finish = () => {
    setClosed(true);
    qc.setQueryData(["settings"], (s) => ({ ...s, tour_done: true }));
    api.saveSettings({ tour_done: true }).catch(() => null);
  };
  const next = () => (step === 2 ? finish() : setStep(step + 1));
  const opts = names.map((c) => ({ key: c, label: c }));
  const S = STEPS[step];

  return (
    <Dialog open onOpenChange={(o) => !o && finish()}>
      <DialogContent className="max-w-md" data-testid="welcome-tour">
        <div className="flex justify-between items-center pr-8">
          <Dots step={step} />
          <div className="flex items-center gap-4">
            <button onClick={finish} className="text-xs text-slate-500 hover:text-slate-300 transition-colors" data-testid="tour-skip-all">Skip tour</button>
            <button onClick={next} className="text-xs text-slate-300 hover:text-white transition-colors" data-testid="tour-next">{step === 2 ? "Done" : "Skip step →"}</button>
          </div>
        </div>
        <span className="w-12 h-12 rounded-2xl grid place-items-center mt-2" style={{ background: `${S.color}22`, color: S.color }}><S.icon size={22} /></span>
        <DialogTitle className="font-display text-2xl" data-testid="tour-step-title">{S.title}</DialogTitle>
        <DialogDescription>{S.hint}</DialogDescription>
        {step === 0 && <SearchPick options={opts} onPick={(o) => { b.toggleCountry(o.label); next(); }} placeholder="e.g. Italy" testId="tour-visited-search" />}
        {step === 1 && <SearchPick options={opts} onPick={(o) => { b.addWish(o.label); next(); }} placeholder="e.g. Japan" testId="tour-wish-search" accent="#F59E0B" />}
        {step === 2 && (
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (dream.trim()) { b.addGoal({ title: dream.trim() }); finish(); } }}>
            <Input autoFocus value={dream} onChange={(e) => setDream(e.target.value)} placeholder="e.g. See the northern lights" data-testid="tour-dream-input" />
            <button type="submit" className="px-4 rounded-md bg-rose-400 text-[#1a0609] font-medium hover:bg-rose-300 transition-colors" data-testid="tour-dream-submit"><ArrowRight size={16} /></button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
